import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory, getLocalizedAgentFeatures, formatPriceWithCurrency, Currency, Language } from '../utils/i18n';
import React, { useState, useEffect } from 'react';
import { 
  Globe, ArrowLeft, ArrowRight, Sparkles, ShieldCheck, FileCheck,
  CreditCard, Landmark, CheckCircle2, UserCheck, Briefcase, FileSpreadsheet,
  Search, Filter, Zap, Scale, Leaf, Coins, Pill, Building2, MapPin
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { RegionalPaymentModal } from './RegionalPaymentModal';

interface EuCenterHubViewProps {
  onLaunchAgent: (target: string, preferredLang?: Language) => void;
  onBack: () => void;
  language: Language;
  currency?: Currency;
  currentUserEmail?: string;
  onSelectLanguage?: (lang: Language) => void;
  onSelectCurrency?: (cur: Currency) => void;
}

export const EU_AGENT_IDS = [
  'ted-eu-procurement-analyst',
  'cbam-carbon-validator',
  'fidic-contract-engineer',
  'legal-arbitration-dispute-counsel',
  'eu-vat-oss-advisor',
  'it-fatturapa-sdi-auditor',
  'es-facturae-verifactu-auditor',
  'pt-saft-fatura-auditor',
  'se-peppol-svefaktura-auditor',
  'de-xrechnung-zugferd-auditor',
  'fr-facturx-chorus-auditor',
  'pharma-eu-dossier-ectd-validator',
  'eu-counterparty-auditor',
  'csrd-esrs-esg-reporter',
  'eudr-deforestation-auditor',
  'eu-ai-act-compliance-auditor',
  'eu-nis2-cybersecurity-auditor',
  'eu-mobility-package-posting-auditor',
  'dora-financial-ict-risk-auditor',
  'dac7-marketplace-tax-auditor',
  'reach-clp-chemical-product-validator',
  'mdr-ivdr-medical-device-auditor',
  'eu-customs-valuation-origin-inspector',
  'dsa-digital-services-act-counsel',
  'csddd-supply-chain-auditor',
  'dpp-product-passport-validator',
  'eu-fsr-foreign-subsidies-counsel',
  'eu-cra-cyber-resilience-auditor',
  'eu-omnibus-ecommerce-auditor'
];

type EuCountryFilter = 'ALL' | 'DE' | 'FR' | 'IT' | 'ES' | 'PT' | 'SE' | 'PL';

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Globe className={className} />;
  return <IconComponent className={className} />;
};

export default function EuCenterHubView({
  onLaunchAgent,
  onBack,
  language = 'en',
  currency = 'EUR',
  currentUserEmail,
  onSelectLanguage,
  onSelectCurrency
}: EuCenterHubViewProps) {
  const [activeLang, setActiveLang] = useState<Language>(language || 'en');
  const [selectedCountry, setSelectedCountry] = useState<EuCountryFilter>('ALL');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPaymentAgent, setSelectedPaymentAgent] = useState<{ id: string; name: string; price: number; singlePrice: number } | null>(null);

  const handleOpenPayment = (id: string, name: string, price: number, singlePrice: number) => {
    setSelectedPaymentAgent({ id, name, price, singlePrice });
    setPaymentModalOpen(true);
  };

  useEffect(() => {
    if (language) setActiveLang(language);
  }, [language]);

  const isUk = activeLang === 'uk';
  const isPl = activeLang === 'pl';
  const isDe = activeLang === 'de';
  const isFr = activeLang === 'fr';
  const isIt = activeLang === 'it';
  const isEs = activeLang === 'es';
  const isPt = activeLang === 'pt';
  const isSv = activeLang === 'sv';
  const isEn = activeLang === 'en';

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [pricing, setPricing] = useState<Record<string, { price: number; limit: number; durationDays: number; singlePrice: number }>>({});

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch('/api/subscription/pricing');
        if (res.ok) {
          const data = await res.json();
          setPricing(data);
        }
      } catch (err) {
        console.error('Failed to fetch EU pricing:', err);
      }
    };
    fetchPricing();
  }, []);

  const agents = agentsConfig.filter((a: any) => EU_AGENT_IDS.includes(a.id));

  const countryPresets: { id: EuCountryFilter; label: string; flag: string; standards: string }[] = [
    { id: 'ALL', flag: '🇪🇺', label: isPl ? 'Wszystkie kraje UE' : isUk ? 'Весь ринок ЄС' : isDe ? 'Gesamte EU' : isFr ? 'Toute l\'UE' : isIt ? 'Tutta l\'UE' : isEs ? 'Toda la UE' : isPt ? 'Toda a UE' : isSv ? 'Hela EU' : 'All EU Market', standards: 'TED • CBAM • FIDIC • GDPR • eIDAS' },
    { id: 'IT', flag: '🇮🇹', label: isPl ? 'Włochy' : isUk ? 'Італія' : 'Italy (IT)', standards: 'FatturaPA (SDI) • Codice Fiscale • Modello F24' },
    { id: 'ES', flag: '🇪🇸', label: isPl ? 'Hiszpania' : isUk ? 'Іспанія' : 'Spain (ES)', standards: 'FacturaE • Veri*Factu • Modelo 303 / 111' },
    { id: 'PT', flag: '🇵🇹', label: isPl ? 'Portugalia' : isUk ? 'Португалія' : 'Portugal (PT)', standards: 'SAF-T (PT) • ATCUD • Fatura Eletrónica' },
    { id: 'SE', flag: '🇸🇪', label: isPl ? 'Szwecja / Nordics' : isUk ? 'Швеція (Nordics)' : 'Sweden / Nordics (SE)', standards: 'PEPPOL BIS 3.0 • Svefaktura • Moms' },
    { id: 'DE', flag: '🇩🇪', label: isPl ? 'Niemcy / DACH' : isUk ? 'Німеччина (DACH)' : 'Germany (DE / DACH)', standards: 'XRechnung 3.0 • ZUGFeRD • Leitweg-ID' },
    { id: 'FR', flag: '🇫🇷', label: isPl ? 'Francja' : isUk ? 'Франція' : 'France (FR)', standards: 'Factur-X • Chorus Pro • TVA CA3' },
    { id: 'PL', flag: '🇵🇱', label: isPl ? 'Polska' : isUk ? 'Польща' : 'Poland (PL)', standards: 'KSeF FA_VAT • JPK_V7 • Biała Lista' }
  ];

  const categories = [
    { id: 'all', label: isPl ? 'Wszystkie moduły UE (29)' : isUk ? 'Усі модулі ЄС (29)' : isDe ? 'Alle EU-Module (29)' : isFr ? 'Tous les modules UE (29)' : isIt ? 'Tutti i moduli UE (29)' : isEs ? 'Todos los módulos UE (29)' : isPt ? 'Todos os módulos UE (29)' : isSv ? 'Alla EU-moduler (29)' : 'All EU Modules (29)' },
    { id: 'einvoicing_tax', label: isPl ? 'e-Faktury & Podatki UE' : isUk ? 'Е-Фактури & Податки ЄС' : isDe ? 'E-Rechnung & Steuern' : isFr ? 'Facturation & TVA' : isIt ? 'FatturaPA & F24' : isEs ? 'FacturaE & IVA' : isPt ? 'SAF-T & Faturação' : isSv ? 'PEPPOL & Skatt' : 'e-Invoicing & Tax' },
    { id: 'esg_env', label: 'CSDDD, DPP, CBAM & ESG' },
    { id: 'tech_cyber', label: 'CRA, AI Act, NIS2 & DORA' },
    { id: 'customs_transport', label: isPl ? 'Transport & Cło UE' : isUk ? 'Транспорт & Митниця ЄС' : 'Transport & Customs' },
    { id: 'tenders_fidic', label: 'TED, FSR & FIDIC' },
    { id: 'pharma_med', label: 'Pharma eCTD & MDR' }
  ];

  const filteredAgents = agents.filter(agent => {
    const name = getLocalizedAgentName(agent, activeLang).toLowerCase();
    const desc = getLocalizedAgentDesc(agent, activeLang).toLowerCase();
    const query = searchQuery.toLowerCase();
    const matchesSearch = !query || name.includes(query) || desc.includes(query);

    if (!matchesSearch) return false;

    // Filter by Country Preset if selected
    if (selectedCountry === 'IT' && !['it-fatturapa-sdi-auditor', 'eu-vat-oss-advisor', 'eu-counterparty-auditor', 'ted-eu-procurement-analyst', 'cbam-carbon-validator', 'fidic-contract-engineer'].includes(agent.id)) {
      if (selectedCategory === 'all') return ['it-fatturapa-sdi-auditor', 'eu-vat-oss-advisor', 'eu-counterparty-auditor', 'ted-eu-procurement-analyst', 'cbam-carbon-validator'].includes(agent.id);
    }
    if (selectedCountry === 'ES' && !['es-facturae-verifactu-auditor', 'eu-vat-oss-advisor', 'eu-counterparty-auditor', 'ted-eu-procurement-analyst', 'cbam-carbon-validator', 'fidic-contract-engineer'].includes(agent.id)) {
      if (selectedCategory === 'all') return ['es-facturae-verifactu-auditor', 'eu-vat-oss-advisor', 'eu-counterparty-auditor', 'ted-eu-procurement-analyst'].includes(agent.id);
    }
    if (selectedCountry === 'PT' && !['pt-saft-fatura-auditor', 'eu-vat-oss-advisor', 'eu-counterparty-auditor', 'ted-eu-procurement-analyst', 'cbam-carbon-validator'].includes(agent.id)) {
      if (selectedCategory === 'all') return ['pt-saft-fatura-auditor', 'eu-vat-oss-advisor', 'eu-counterparty-auditor'].includes(agent.id);
    }
    if (selectedCountry === 'SE' && !['se-peppol-svefaktura-auditor', 'eu-vat-oss-advisor', 'eu-counterparty-auditor', 'ted-eu-procurement-analyst', 'cbam-carbon-validator'].includes(agent.id)) {
      if (selectedCategory === 'all') return ['se-peppol-svefaktura-auditor', 'eu-vat-oss-advisor', 'eu-counterparty-auditor'].includes(agent.id);
    }
    if (selectedCountry === 'DE' && !['de-xrechnung-zugferd-auditor', 'eu-vat-oss-advisor', 'eu-counterparty-auditor', 'ted-eu-procurement-analyst', 'cbam-carbon-validator', 'fidic-contract-engineer'].includes(agent.id)) {
      if (selectedCategory === 'all') return ['de-xrechnung-zugferd-auditor', 'eu-vat-oss-advisor', 'eu-counterparty-auditor', 'cbam-carbon-validator'].includes(agent.id);
    }
    if (selectedCountry === 'FR' && !['fr-facturx-chorus-auditor', 'eu-vat-oss-advisor', 'eu-counterparty-auditor', 'ted-eu-procurement-analyst', 'cbam-carbon-validator'].includes(agent.id)) {
      if (selectedCategory === 'all') return ['fr-facturx-chorus-auditor', 'eu-vat-oss-advisor', 'eu-counterparty-auditor'].includes(agent.id);
    }

    if (selectedCategory === 'all') return true;
    if (selectedCategory === 'einvoicing_tax') return ['it-fatturapa-sdi-auditor', 'es-facturae-verifactu-auditor', 'pt-saft-fatura-auditor', 'se-peppol-svefaktura-auditor', 'de-xrechnung-zugferd-auditor', 'fr-facturx-chorus-auditor', 'eu-vat-oss-advisor', 'dac7-marketplace-tax-auditor', 'eu-counterparty-auditor', 'legal-arbitration-dispute-counsel', 'eu-omnibus-ecommerce-auditor'].includes(agent.id);
    if (selectedCategory === 'esg_env') return ['cbam-carbon-validator', 'csrd-esrs-esg-reporter', 'eudr-deforestation-auditor', 'reach-clp-chemical-product-validator', 'csddd-supply-chain-auditor', 'dpp-product-passport-validator'].includes(agent.id);
    if (selectedCategory === 'tech_cyber') return ['eu-ai-act-compliance-auditor', 'eu-nis2-cybersecurity-auditor', 'dora-financial-ict-risk-auditor', 'dsa-digital-services-act-counsel', 'eu-cra-cyber-resilience-auditor'].includes(agent.id);
    if (selectedCategory === 'customs_transport') return ['eu-mobility-package-posting-auditor', 'eu-customs-valuation-origin-inspector'].includes(agent.id);
    if (selectedCategory === 'tenders_fidic') return ['ted-eu-procurement-analyst', 'fidic-contract-engineer', 'eu-fsr-foreign-subsidies-counsel'].includes(agent.id);
    if (selectedCategory === 'pharma_med') return ['pharma-eu-dossier-ectd-validator', 'mdr-ivdr-medical-device-auditor'].includes(agent.id);
    return true;
  });

  const activeCountryPreset = countryPresets.find(c => c.id === selectedCountry) || countryPresets[0];

  return (
    <div className="space-y-6 animate-fade-in pb-12 font-mono max-w-7xl mx-auto">
      {/* Top Navigation & Language Switcher */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <button
          onClick={onBack}
          className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{isPl ? '← Powrót do Panelu Głównego' : isUk ? '← Назад до Головного Хабу' : isDe ? '← Zurück zum Haupt-Hub' : isFr ? '← Retour au Hub Principal' : isIt ? '← Torna all\'Hub Principale' : isEs ? '← Volver al Hub Principal' : isPt ? '← Voltar ao Hub Principal' : isSv ? '← Tillbaka till Huvudhubben' : '← Back to Main Hub'}</span>
        </button>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Native Language Switcher with all 9 languages */}
          <div className="flex items-center gap-1 bg-slate-950 p-1 border border-slate-700 rounded-lg flex-wrap">
            <button
              onClick={() => { setActiveLang('en'); onSelectLanguage?.('en'); onSelectCurrency?.('EUR'); }}
              className={`px-2 py-0.5 text-[10px] font-bold rounded cursor-pointer transition-all ${activeLang === 'en' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'}`}
            >
              EN
            </button>
            <button
              onClick={() => { setActiveLang('de'); onSelectLanguage?.('de'); onSelectCurrency?.('EUR'); }}
              className={`px-2 py-0.5 text-[10px] font-bold rounded cursor-pointer transition-all ${activeLang === 'de' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'}`}
            >
              DE
            </button>
            <button
              onClick={() => { setActiveLang('fr'); onSelectLanguage?.('fr'); onSelectCurrency?.('EUR'); }}
              className={`px-2 py-0.5 text-[10px] font-bold rounded cursor-pointer transition-all ${activeLang === 'fr' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'}`}
            >
              FR
            </button>
            <button
              onClick={() => { setActiveLang('pl'); onSelectLanguage?.('pl'); onSelectCurrency?.('PLN'); }}
              className={`px-2 py-0.5 text-[10px] font-bold rounded cursor-pointer transition-all ${activeLang === 'pl' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'}`}
            >
              PL
            </button>
            <button
              onClick={() => { setActiveLang('uk'); onSelectLanguage?.('uk'); onSelectCurrency?.('UAH'); }}
              className={`px-2 py-0.5 text-[10px] font-bold rounded cursor-pointer transition-all ${activeLang === 'uk' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'}`}
            >
              UA
            </button>
            <button
              onClick={() => { setActiveLang('it'); onSelectLanguage?.('it'); onSelectCurrency?.('EUR'); }}
              className={`px-2 py-0.5 text-[10px] font-bold rounded cursor-pointer transition-all ${activeLang === 'it' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'}`}
            >
              IT
            </button>
            <button
              onClick={() => { setActiveLang('es'); onSelectLanguage?.('es'); onSelectCurrency?.('EUR'); }}
              className={`px-2 py-0.5 text-[10px] font-bold rounded cursor-pointer transition-all ${activeLang === 'es' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'}`}
            >
              ES
            </button>
            <button
              onClick={() => { setActiveLang('pt'); onSelectLanguage?.('pt'); onSelectCurrency?.('EUR'); }}
              className={`px-2 py-0.5 text-[10px] font-bold rounded cursor-pointer transition-all ${activeLang === 'pt' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'}`}
            >
              PT
            </button>
            <button
              onClick={() => { setActiveLang('sv'); onSelectLanguage?.('sv'); onSelectCurrency?.('SEK'); }}
              className={`px-2 py-0.5 text-[10px] font-bold rounded cursor-pointer transition-all ${activeLang === 'sv' ? 'bg-blue-600 text-white shadow-xs' : 'text-slate-400 hover:text-white'}`}
            >
              SV
            </button>
          </div>

          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-amber-500/20 text-amber-300 border border-amber-400/30 rounded-md flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5 text-amber-400" />
            <span>{activeCountryPreset.standards}</span>
          </span>
        </div>
      </div>

      {/* Country / Jurisdiction Bar Selector */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-3 sm:p-4 text-white shadow-lg space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-[11px] font-bold text-slate-400 uppercase flex items-center gap-1.5 font-mono">
            <MapPin className="w-3.5 h-3.5 text-blue-400" />
            <span>{isPl ? 'Wybierz kraj / jurysdykcję docelową:' : isUk ? 'Оберіть країну / цільову юрисдикцію ЄС:' : isDe ? 'Zieljurisdiktion / Land wählen:' : isFr ? 'Sélectionnez la juridiction cible :' : isIt ? 'Seleziona il Paese / giurisdizione UE:' : isEs ? 'Seleccionar país / jurisdicción UE:' : isPt ? 'Selecionar país / jurisdição UE:' : isSv ? 'Välj land / EU-jurisdiktion:' : 'Select Target EU Country / Jurisdiction:'}</span>
          </span>
          <span className="text-[10px] text-blue-300 font-mono">
            {isPl ? 'Automatyczna adaptacja norm i rejestrów' : isUk ? 'Авто-адаптація реєстрів та форматів фактур' : 'Auto-adapts registries & invoice formats'}
          </span>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
          {countryPresets.map(preset => (
            <button
              key={preset.id}
              onClick={() => setSelectedCountry(preset.id)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold font-mono transition-all cursor-pointer flex items-center gap-1.5 shrink-0 ${
                selectedCountry === preset.id
                  ? 'bg-blue-600 text-white shadow-md shadow-blue-900/50 border border-blue-400/50'
                  : 'bg-slate-800/80 text-slate-300 hover:bg-slate-700 hover:text-white border border-slate-700'
              }`}
            >
              <span>{preset.flag}</span>
              <span>{preset.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-slate-950 via-blue-950/90 to-indigo-950 border-2 border-blue-500/50 rounded-2xl p-6 sm:p-8 text-white shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-4xl space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/20 border border-blue-400/40 rounded-full text-[11px] font-bold text-blue-300 uppercase tracking-wider">
            <span>{activeCountryPreset.flag} {activeCountryPreset.label} • {activeCountryPreset.standards}</span>
          </div>

          <h1 className="text-2xl sm:text-4xl font-extrabold uppercase tracking-tight text-white flex items-center gap-3">
            <span>{isPl ? 'CENTRUM OPERACYJNE DLA UNII EUROPEJSKIEJ' : isUk ? 'ЄВРОПЕЙСЬКИЙ ОПЕРАЦІЙНИЙ ЦЕНТР' : isDe ? 'EU-OPERATIONSZENTRUM & BINNENMARKT' : isFr ? 'CENTRE OPÉRATIONNEL UNION EUROPÉENNE' : isIt ? 'CENTRO OPERATIVO UNIONE EUROPEA' : isEs ? 'CENTRO OPERATIVO UNIÓN EUROPEA' : isPt ? 'CENTRO OPERACIONAL UNIÃO EUROPEIA' : isSv ? 'EU OPERATIVT CENTER & INRE MARKNAD' : 'EU ENTERPRISE & SINGLE MARKET HUB'}</span>
          </h1>

          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
            {isPl
              ? 'Wyspecjalizowana platforma analityczna dla firm operujących na wspólnym rynku UE: monitoring przetargów TED Europa i ESPD XML, kalkulacja opłat węglowych CBAM (CO₂), inżynieria kontraktowa FIDIC i certyfikaty IPC, arbitraż GAFTA/FOSFA, rozliczenia VAT OSS i rejestracja leków eCTD EMA.'
              : isUk
              ? 'Спеціалізована аналітична платформа для підприємств, що експортують та працюють на ринку Євросоюзу: моніторинг тендерів TED EU та ESPD XML, розрахунок вуглецевого коригування CBAM (CO₂), адміністрування контрактів FIDIC та сертифікатів IPC, міжнародний арбітраж GAFTA/FOSFA, транскордонний ПДВ VAT OSS та реєстрація ліків eCTD EMA.'
              : isDe
              ? 'Spezialisierte Compliance-Plattform für den EU-Binnenmarkt: Ausschreibungen TED Europa & ESPD XML, CBAM-CO₂-Berechnung, FIDIC-Vertragsmanagement & IPC, GAFTA/FOSFA-Schiedsgerichtsbarkeit, VAT OSS und eCTD EMA Pharma-Dossiers.'
              : isFr
              ? 'Plateforme de conformité pour les entreprises opérant sur le marché unique européen : suivi des marchés publics TED & ESPD XML, calcul taxe carbone CBAM (CO₂), contrats FIDIC & certificats IPC, arbitrage GAFTA/FOSFA, TVA OSS et dossiers eCTD EMA.'
              : isIt
              ? 'Piattaforma di conformità per le imprese sul mercato unico UE: gare d\'appalto TED Europa & ESPD XML, calcolo carbon tax CBAM (CO₂), contratti FIDIC & certificati IPC, arbitrato GAFTA/FOSFA, IVA OSS e dossier eCTD EMA.'
              : isEs
              ? 'Plataforma de cumplimiento para empresas en el mercado único de la UE: licitaciones TED Europa & ESPD XML, cálculo de ajuste de carbono CBAM (CO₂), contratos FIDIC & IPC, arbitraje GAFTA/FOSFA, IVA OSS y dosieres eCTD EMA.'
              : isPt
              ? 'Plataforma de conformidade empresarial para o mercado único da UE: concursos públicos TED Europa & ESPD XML, cálculo da taxa de carbono CBAM (CO₂), contratos FIDIC & IPC, arbitragem GAFTA/FOSFA, IVA OSS e dossiês eCTD EMA.'
              : isSv
              ? 'Efterlevnadsplattform för företag på EU:s inre marknad: offentlig upphandling TED Europa & ESPD XML, beräkning av koldioxidavgifter CBAM, FIDIC-avtal & IPC-certifikat, GAFTA/FOSFA-skiljeförfarande, Moms OSS och eCTD EMA.'
              : 'Enterprise compliance platform for organizations operating across the European Single Market: TED Europa tender tracking & ESPD XML authoring, CBAM carbon border tax calculation, FIDIC engineering & IPC certificates, GAFTA/FOSFA arbitration, VAT OSS returns, and EMA eCTD pharma dossiers.'}
          </p>

          {/* Quick Hub Pass Promo */}
          <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-slate-900/90 border border-blue-500/40 rounded-xl">
            <div className="space-y-1">
              <div className="text-xs font-bold text-blue-300 uppercase flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-blue-400" />
                <span>{isPl ? 'PAKIET KOMPLEKSOWY: EU ENTERPRISE PASS' : isUk ? 'КОМПЛЕКСНИЙ ПАКЕТ: EU ENTERPRISE PASS' : 'ALL-INCLUSIVE: EU ENTERPRISE PASS'}</span>
              </div>
              <div className="text-[11px] text-slate-300">
                {isPl
                  ? 'Dostęp do wszystkich 23 modułów UE (200 zapytań/miesiąc) • Stripe / Apple Pay / SEPA'
                  : isUk
                  ? 'Повний доступ до всіх 23 модулів Євросоюзу (200 запитів/міс) • Stripe / Apple Pay / SEPA'
                  : 'Full access to all 23 EU modules (200 requests/month) • Stripe / Apple Pay / SEPA'}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xl font-extrabold text-white font-mono">€399<span className="text-xs text-blue-300 font-normal"> / {isPl ? 'miesiąc' : isUk ? 'міс' : isDe ? 'Monat' : isFr ? 'mois' : isIt ? 'mese' : isEs ? 'mes' : isPt ? 'mês' : isSv ? 'månad' : 'mo'}</span></span>
              <button
                onClick={() => onLaunchAgent('ted-eu-procurement-analyst', activeLang)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs uppercase rounded-lg transition-all shadow-md shadow-blue-900/40 cursor-pointer flex items-center gap-1.5"
              >
                <Zap className="w-3.5 h-3.5" />
                <span>{isPl ? 'Aktywuj Pakiet' : isUk ? 'Активувати Хаб' : isDe ? 'Pass aktivieren' : isFr ? 'Activer le Pass' : isIt ? 'Attiva il Pass' : isEs ? 'Activar Pass' : isPt ? 'Ativar Pass' : isSv ? 'Aktivera Pass' : 'Activate Pass'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white border border-slate-200 rounded-xl p-4 shadow-xs">
        {/* Search */}
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={isPl ? 'Szukaj modułu unijnego...' : isUk ? 'Пошук європейського модуля...' : isDe ? 'EU-Modul suchen...' : isFr ? 'Rechercher un module UE...' : isIt ? 'Cerca modulo UE...' : isEs ? 'Buscar módulo UE...' : isPt ? 'Pesquisar módulo UE...' : isSv ? 'Sök EU-modul...' : 'Search EU tool...'}
            className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
          />
        </div>

        {/* Category Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-1 md:pb-0 scrollbar-none">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold font-mono uppercase whitespace-nowrap transition-all cursor-pointer ${
                selectedCategory === cat.id
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAgents.map(agent => {
          const name = getLocalizedAgentName(agent, activeLang);
          const desc = getLocalizedAgentDesc(agent, activeLang);
          const features = getLocalizedAgentFeatures(agent, activeLang);

          // Pricing according to 98% margin tri-regional rule
          let priceEur = 49;
          let singlePriceEur = 9;
          if (['cbam-carbon-validator', 'fidic-contract-engineer', 'legal-arbitration-dispute-counsel', 'csrd-esrs-esg-reporter', 'eudr-deforestation-auditor', 'pharma-eu-dossier-ectd-validator', 'dora-financial-ict-risk-auditor', 'reach-clp-chemical-product-validator', 'mdr-ivdr-medical-device-auditor'].includes(agent.id)) {
            priceEur = 99;
            singlePriceEur = 19;
          } else if (agent.id === 'eu-counterparty-auditor') {
            priceEur = 19;
            singlePriceEur = 3;
          }

          return (
            <div
              key={agent.id}
              className="bg-white border border-slate-200 hover:border-blue-400/60 rounded-2xl p-6 shadow-xs hover:shadow-xl transition-all flex flex-col justify-between gap-5 relative group font-mono"
            >
              <div className="space-y-4">
                {/* Header Badge */}
                <div className="flex items-center justify-between gap-2">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 group-hover:scale-105 transition-transform">
                    <AgentIcon name={agent.icon} className="w-5 h-5" />
                  </div>
                  <div 
                    onClick={() => handleOpenPayment(agent.id, name, priceEur, singlePriceEur)}
                    className="text-right cursor-pointer hover:opacity-85 transition-opacity bg-blue-50/60 hover:bg-blue-100/80 p-2 rounded-xl border border-blue-200/60"
                    title={isPl ? 'Kliknij, aby opłacić pakiet' : isUk ? 'Натисніть для вибору тарифу' : 'Click to select pricing plan'}
                  >
                    <div className="text-xs font-extrabold text-slate-900 font-mono">
                      €{singlePriceEur} <span className="text-[10px] text-slate-500 font-normal">/ {isPl ? 'uruchomienie' : isUk ? 'запуск' : isDe ? 'Abruf' : isFr ? 'requête' : isIt ? 'richiesta' : isEs ? 'consulta' : isPt ? 'pedido' : isSv ? 'körning' : 'run'}</span>
                    </div>
                    <div className="text-[10px] text-emerald-600 font-bold">
                      €{priceEur} / 30 {isPl ? 'dni (30 zapyt.)' : isUk ? 'дн (30 зап.)' : 'days (30 req.)'}
                    </div>
                  </div>
                </div>

                {/* Title & Description */}
                <div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight group-hover:text-blue-600 transition-colors leading-snug">
                    {name}
                  </h3>
                  <p className="text-xs text-slate-600 mt-2 leading-relaxed line-clamp-3">
                    {desc}
                  </p>
                </div>

                {/* Features List */}
                {features && features.length > 0 && (
                  <div className="space-y-1.5 pt-2 border-t border-slate-100">
                    {features.slice(0, 3).map((feat, idx) => (
                      <div key={idx} className="flex items-start gap-1.5 text-[10px] text-slate-500">
                        <CheckCircle2 className="w-3.5 h-3.5 text-blue-500 shrink-0 mt-0.5" />
                        <span>{feat}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="pt-4 border-t border-slate-100 flex items-center gap-2">
                <button
                  onClick={() => onLaunchAgent(agent.id, activeLang)}
                  className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold uppercase transition-all shadow-xs hover:shadow-md flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Zap className="w-3.5 h-3.5" />
                  <span>{isPl ? '🚀 Uruchom Moduł' : isUk ? '🚀 Відкрити Модуль' : isDe ? '🚀 Modul öffnen' : isFr ? '🚀 Ouvrir le Module' : isIt ? '🚀 Apri Modulo' : isEs ? '🚀 Abrir Módulo' : isPt ? '🚀 Abrir Módulo' : isSv ? '🚀 Öppna Modul' : '🚀 Open Module'}</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Regional Payment Modal */}
      {selectedPaymentAgent && (
        <RegionalPaymentModal
          isOpen={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          agentId={selectedPaymentAgent.id}
          agentName={selectedPaymentAgent.name}
          currency={currency}
          userEmail={currentUserEmail}
          initialPrice={selectedPaymentAgent.price}
          initialSinglePrice={selectedPaymentAgent.singlePrice}
          jurisdiction={selectedCountry === 'ALL' ? 'EU' : (selectedCountry as any)}
        />
      )}
    </div>
  );
}
